import { ReviewTileItem } from "@/types/tile.types";
import type {
  TileTypes,
  RiskPoint,
  Ambiguity,
  ConflictingTerm,
  ComplexLanguage,
  NegotiationPoint,
  NonStandardClause,
  UndefinedTerm,
  Placeholder,
  CrossReferencingClause,
  SequentialOrder,
  MissingClause,
  Inconsistency,
  CustomReview,
} from "@/types/review.types";

const getFieldText = (label: string, value: string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "";
  return `${label}: ${value}\n`;
};

const getObjectFieldText = (label: string, value: unknown): string => {
  if (value === undefined || value === null || value === "") return "";
  const stringValue = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
  return `${label}: ${stringValue}\n`;
};

function formatRiskPoints(item: RiskPoint): string {
  return (
    getFieldText("Clause", item.clause) +
    getFieldText("Risk Text", item.riskText) +
    getFieldText("Risk Explanation", item.riskExplanation) +
    getFieldText("Suggestion", item.suggestions)
  );
}

function formatAmbiguities(item: Ambiguity): string {
  return (
    getFieldText("Clause", item.clause) +
    getFieldText("Ambiguous Text", item.ambiguousText) +
    getFieldText("Reason", item.reason) +
    getFieldText("Suggestion", item.suggestedclause)
  );
}

function formatConflictingTerms(item: ConflictingTerm): string {
  return (
    getObjectFieldText("Clause Number", item.clause1?.number) +
    getObjectFieldText("Clause Number", item.clause2?.number) +
    getObjectFieldText("Relevant Text", item.clause1?.relevant_text) +
    getFieldText("Reason", item.reason) +
    getFieldText("Suggestion", item.suggestedclause)
  );
}

function formatComplexLanguage(item: ComplexLanguage): string {
  return (
    getFieldText("Clause", item.clause) +
    getFieldText("Complex Text", item.complexText) +
    getFieldText("Reason", item.reason) +
    getFieldText("Suggestions", item.suggestions)
  );
}

function formatNegotiationPoints(item: NegotiationPoint): string {
  if (item.email !== undefined) {
    return getFieldText("Email", item.email);
  }
  return (
    getFieldText("Clause", item.clause) +
    getFieldText("Negotiation Text", item.negotiationText) +
    getFieldText("Points", item.points) +
    getFieldText("Suggestion", item.suggestion)
  );
}

function formatSimpleReviewTypes(item: TileTypes, tileType: string): string {
  switch (tileType) {
    case "nonStandardClauses": {
      const t = item as NonStandardClause;
      return (
        getFieldText("Clause", t.clause) +
        getFieldText("Non-Standard Text", t.nonStandardText) +
        getFieldText("Reason", t.reason) +
        getFieldText("Suggestion", t.suggestedclause)
      );
    }
    case "undefinedTerms": {
      const t = item as UndefinedTerm;
      return (
        getFieldText("Context", t.contextText) +
        getFieldText("Reason", t.reason) +
        getFieldText("Suggestion", t.suggestedclause)
      );
    }
    case "placeholders":
      return getFieldText("Placeholder", (item as Placeholder).placeholder);
    case "crossReferencingClauses": {
      const t = item as CrossReferencingClause;
      return (
        getFieldText("Clause", t.clause) +
        getFieldText("Context", t.context) +
        getFieldText("Suggestion", t.suggestion)
      );
    }
    case "sequentialOrder": {
      const t = item as SequentialOrder;
      return (
        getFieldText("Current Number", t.currentNumber) +
        getFieldText("Context", t.context) +
        getFieldText("Suggested Number", t.suggestedNumber) +
        getFieldText("Reason", t.reason)
      );
    }
    case "missingClauses": {
      const t = item as MissingClause;
      return (
        getFieldText("Clause", t.clause) +
        getFieldText("Reason", t.reason) +
        getFieldText("Suggestion", t.suggestedclause) +
        getFieldText("Insertion Point Under", t.insertionPoint)
      );
    }
    default:
      return "";
  }
}

function formatInconsistencies(item: Inconsistency): string {
  let content = "";
  if (item.document1 !== undefined && item.document2 !== undefined) {
    content += getFieldText("Document 1", item.document1);
    content += getFieldText("Document 2", item.document2);
  }
  return (
    content +
    getFieldText("Text 1", item.text1) +
    getFieldText("Text 2", item.text2) +
    getFieldText("Explanation", item.explanation) +
    getFieldText("Suggestion", item.suggestion)
  );
}

function formatCustomReview(item: CustomReview): string {
  return (
    getFieldText("Clause", item.clause) +
    getFieldText("Relevant Text", item.relevantText) +
    getFieldText("Reason", item.reason) +
    getFieldText("Suggestion", item.suggestion)
  );
}

function getFallbackText(item: TileTypes): string {
  if ("reason" in item && item.reason !== undefined) return item.reason;
  if ("analysis" in item && item.analysis !== undefined) return item.analysis;
  if ("explanation" in item && item.explanation !== undefined) return item.explanation;
  return "No content available";
}

export const getTileContentAsText = (tile: ReviewTileItem): string => {
  const item = tile.originalData;
  const tileType = tile.originalKey;
  if (item === undefined) return "";

  let content = "";
  if ("documentName" in item && item.documentName !== undefined) {
    content += getFieldText("Document Name", item.documentName);
  }

  switch (tileType) {
    case "riskPoints":
      content += formatRiskPoints(item as RiskPoint);
      break;
    case "ambiguities":
      content += formatAmbiguities(item as Ambiguity);
      break;
    case "conflictingTerms":
      content += formatConflictingTerms(item as ConflictingTerm);
      break;
    case "complexLanguage":
      content += formatComplexLanguage(item as ComplexLanguage);
      break;
    case "negotiationPoints":
      content += formatNegotiationPoints(item as NegotiationPoint);
      break;
    case "inconsistencies":
      content += formatInconsistencies(item as Inconsistency);
      break;
    case "customReview":
      content += formatCustomReview(item as CustomReview);
      break;
    default: {
      const simple = formatSimpleReviewTypes(item, tileType ?? "");
      content += simple !== "" ? simple : getFallbackText(item);
    }
  }

  return content.trim();
};
